/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  computed,
  ref,
  reactive,
  type Component,
  type VNodeProps,
  type AllowedComponentProps,
  markRaw,
} from 'vue'

export type StellaModalType = 'modal' | 'overlay-panel'
export type StellaModalCloseType = 'x' | 'content' | 'footer'
interface CommonConfig {
  strict?: boolean
  type?: StellaModalType
}

type KeysOfType<T, U> = {
  [K in keyof T]: T[K] extends U ? K : never
}[keyof T]
type RequiredKeys<T> = Exclude<
  KeysOfType<T, Exclude<T[keyof T], undefined>>,
  undefined
>
type ExcludeOptionalProps<T> = Pick<T, RequiredKeys<T>>
type HasAtLeastOneProperty<T, U = { [K in keyof T]: Pick<T, K> }> = Partial<T> &
  U[keyof U]

type ComponentProps<C extends Component> = C extends new (...args: any) => any
  ? Omit<
      InstanceType<C>['$props'],
      keyof VNodeProps | keyof AllowedComponentProps
    >
  : never
type ComponentEmits<C extends Component> = C extends new (...args: any) => any
  ? Omit<
      InstanceType<C>['$emits'],
      keyof VNodeProps | keyof AllowedComponentProps
    >
  : never

type ComponentData<C extends Component> = ComponentProps<C> &
  ComponentEmits<C> & { onDismiss?: () => void }
type OpenConfig<C extends Component> =
  HasAtLeastOneProperty<ComponentData<C>> extends never
    ? {
        content: C
      }
    : HasAtLeastOneProperty<
          ExcludeOptionalProps<ComponentData<C>>
        > extends never
      ? {
          content: C
          data?: ComponentData<C>
        }
      : {
          content: C
          data: ComponentData<C>
        }

const state = reactive({
  isOpen: false,
  config: {
    strict: false,
    type: 'modal',
  },
  componentProps: null as any,
  component: null as any,
})
const justOpened = ref(false)

function toggle(bool = !state.isOpen) {
  state.isOpen = bool
}

function open<C extends Component>(opts: OpenConfig<C> & CommonConfig) {
  if (opts.content.name !== 'AsyncComponentWrapper') {
    throw Error(
      'StellaModalError: Please do not pass statically imported content components. Use defineAsyncComponent() instead.',
    )
  }
  justOpened.value = true
  if ('data' in opts) state.componentProps = markRaw(opts.data!)
  state.component = markRaw(opts.content)
  state.config.strict = Boolean(opts.strict)
  state.config.type = opts.type ?? 'modal'
  toggle(true)
  setTimeout(() => {
    justOpened.value = false
  }, 10)
}

function close() {
  if (justOpened.value) return
  toggle(false)
}

export function _useStellaModalState() {
  return state
}

export const stellaModal = {
  open,
  close,
  isOpen: computed(() => state.isOpen),
}
